import React from 'react';
import styled, { keyframes } from 'styled-components';
import { brandTheme } from '../styles/BrandTheme';

// WhatsApp phone number - replace with your actual WhatsApp number
const WHATSAPP_NUMBER = '447535935779'; // Format: country code + number without + or spaces
const DEFAULT_MESSAGE = 'Hi! I\'m interested in your resin surfacing services. Could you please provide more information?';

// Animation for the floating effect
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Animation for the pulse effect
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(37, 211, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

// Animation for the welcome message slide-in
const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Animation for the welcome message fade out
const fadeOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-10px) scale(0.9);
  }
`;

const WidgetContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  animation: ${float} 3s ease-in-out infinite;
  
  &:hover {
    animation-play-state: paused;
  }

  @media (max-width: 480px) {
    bottom: 16px;
    right: 16px;
  }

  @media (max-width: 390px) {
    bottom: 12px;
    right: 12px;
  }

  @media (max-width: 375px) {
    bottom: 10px;
    right: 10px;
  }
`;


const WhatsAppIcon = styled.svg`
  width: 32px;
  height: 32px;
  fill: white;

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
  }

  @media (max-width: 390px) {
    width: 26px;
    height: 26px;
  }

  @media (max-width: 375px) {
    width: 24px;
    height: 24px;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  right: 75px;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 1001;

  &::before {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-left-color: #2d2d2d;
    filter: drop-shadow(2px 0 4px rgba(0, 0, 0, 0.2));
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const WhatsAppButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25d366 0%, #20c55a 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  transition: all 0.3s ease;
  position: relative;
  animation: ${pulse} 2s infinite;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 30px rgba(37, 211, 102, 0.6);
    animation: none;
    
    ${Tooltip} {
      opacity: 1;
      visibility: visible;
      transform: translateY(-50%) translateX(-8px);
    }
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
  }

  @media (max-width: 390px) {
    width: 52px;
    height: 52px;
  }

  @media (max-width: 375px) {
    width: 48px;
    height: 48px;
  }
`;

const TooltipText = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '💬';
    font-size: 16px;
  }
`;

const WelcomeMessage = styled.div`
  position: absolute;
  bottom: 80px;
  right: 0;
  background: linear-gradient(135deg, #25d366 0%, #20c55a 100%);
  color: white;
  padding: 16px 20px;
  border-radius: 20px 20px 20px 8px;
  font-size: 14px;
  font-weight: 400;
  max-width: 280px;
  box-shadow: 0 8px 32px rgba(37, 211, 102, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  z-index: 1002;
  animation: ${slideIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 0;
  transform: translateY(20px) scale(0.8);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    right: 20px;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-top-color: #20c55a;
    border-right-color: #20c55a;
  }

  @media (max-width: 480px) {
    max-width: 220px;
    padding: 12px 14px;
    font-size: 12px;
    bottom: 72px;
    right: -5px;
    
    &::after {
      right: 15px;
      border-width: 6px;
    }
  }

  @media (max-width: 390px) {
    max-width: 200px;
    padding: 10px 12px;
    font-size: 11px;
    bottom: 68px;
    right: -8px;
    border-radius: 16px 16px 16px 6px;
    
    &::after {
      right: 12px;
      border-width: 6px;
    }
  }

  @media (max-width: 375px) {
    max-width: 180px;
    padding: 8px 10px;
    font-size: 10px;
    bottom: 64px;
    right: -10px;
    border-radius: 14px 14px 14px 5px;
    
    &::after {
      right: 10px;
      border-width: 5px;
    }
  }

  @media (max-width: 320px) {
    max-width: 160px;
    padding: 6px 8px;
    font-size: 9px;
    bottom: 60px;
    right: -12px;
    border-radius: 12px 12px 12px 4px;
    
    &::after {
      right: 8px;
      border-width: 4px;
    }
  }
`;

const WelcomeText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  
  &::before {
    content: '👋';
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    
    &::before {
      font-size: 16px;
    }
  }

  @media (max-width: 390px) {
    gap: 6px;
    
    &::before {
      font-size: 14px;
    }
  }

  @media (max-width: 375px) {
    gap: 5px;
    
    &::before {
      font-size: 12px;
    }
  }

  @media (max-width: 320px) {
    gap: 4px;
    
    &::before {
      font-size: 10px;
    }
  }
`;

const WelcomeContent = styled.div`
  line-height: 1.4;

  @media (max-width: 480px) {
    line-height: 1.3;
  }

  @media (max-width: 390px) {
    line-height: 1.2;
  }

  @media (max-width: 375px) {
    line-height: 1.1;
  }

  @media (max-width: 320px) {
    line-height: 1.0;
  }
`;

const WelcomeTitle = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
  font-size: 15px;

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 3px;
  }

  @media (max-width: 390px) {
    font-size: 12px;
    margin-bottom: 2px;
  }

  @media (max-width: 375px) {
    font-size: 11px;
    margin-bottom: 2px;
  }

  @media (max-width: 320px) {
    font-size: 10px;
    margin-bottom: 1px;
  }
`;

const WelcomeSubtext = styled.div`
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 11px;
    line-height: 1.2;
  }

  @media (max-width: 390px) {
    font-size: 10px;
    line-height: 1.1;
  }

  @media (max-width: 375px) {
    font-size: 9px;
    line-height: 1.1;
  }

  @media (max-width: 320px) {
    font-size: 8px;
    line-height: 1.0;
  }
`;

const WhatsAppWidget: React.FC = () => {
  const [showWelcome, setShowWelcome] = React.useState(true);
  const [isHovering, setIsHovering] = React.useState(false);

  React.useEffect(() => {
    // Show welcome message for 10 seconds, then hide it
    const timer = setTimeout(() => {
      setShowWelcome(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(DEFAULT_MESSAGE);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <WidgetContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showWelcome && (
        <WelcomeMessage>
          <WelcomeText>
            <WelcomeContent>
              <WelcomeTitle>Need resin surfacing help?</WelcomeTitle>
              <WelcomeSubtext>
                Free quotes & expert advice!
              </WelcomeSubtext>
            </WelcomeContent>
          </WelcomeText>
        </WelcomeMessage>
      )}
      
      <WhatsAppButton onClick={handleWhatsAppClick} aria-label="Chat with us on WhatsApp">
        <WhatsAppIcon viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </WhatsAppIcon>
        <Tooltip>
          <TooltipText>
            Chat with us on WhatsApp
          </TooltipText>
        </Tooltip>
      </WhatsAppButton>
    </WidgetContainer>
  );
};

export default WhatsAppWidget;
